import { useState } from "preact/hooks";

export interface SpecInputProps {
  onSubmit: (spec: unknown, label: string) => Promise<void>;
}

const EXAMPLE_URLS: { label: string; url: string }[] = [
  { label: "Swagger Petstore v3", url: "https://petstore3.swagger.io/api/v3/openapi.json" },
  { label: "APIs.guru — XKCD",   url: "https://api.apis.guru/v2/specs/xkcd.com/1.0.0/openapi.json" },
  { label: "APIs.guru — IPify",  url: "https://api.apis.guru/v2/specs/ipify.org/1.0.0/openapi.json" }
];

export function SpecInput(props: SpecInputProps) {
  const [url, setUrl] = useState("");
  const [paste, setPaste] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const submit = async (spec: unknown, label: string): Promise<void> => {
    setBusy(true);
    setError(undefined);
    try {
      await props.onSubmit(spec, label);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const submitUrl = async (which: string): Promise<void> => {
    const target = which.trim();
    if (!target) { setError("Enter a URL or paste a spec."); return; }
    setBusy(true);
    setError(undefined);
    try {
      const res = await fetch("/api/fetch-spec", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ url: target })
      });
      const data = await res.json() as { error?: string; spec?: unknown; url?: string };
      if (!res.ok) throw new Error(data.error ?? `fetch-spec returned ${res.status}`);
      const title = (data.spec as { info?: { title?: string } })?.info?.title ?? target;
      await props.onSubmit(data.spec, title);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const submitPaste = async (): Promise<void> => {
    const txt = paste.trim();
    if (txt.length === 0) { setError("Paste an OpenAPI JSON spec."); return; }
    let parsed: unknown;
    try { parsed = JSON.parse(txt); }
    catch (e) { setError("Not valid JSON: " + (e as Error).message.slice(0, 120)); return; }
    const o = parsed as { openapi?: unknown; info?: { title?: string }; paths?: unknown; webhooks?: unknown };
    if (!o.openapi || !o.info || (!o.paths && !o.webhooks)) {
      setError("Missing top-level openapi / info / (paths|webhooks). Need an OpenAPI 3.x JSON document.");
      return;
    }
    await submit(parsed, o.info.title ?? "pasted-spec");
  };

  return (
    <section>
      <h1 style={{ fontFamily: "monospace", letterSpacing: 2, marginBottom: 4 }}>KEYSTONE</h1>
      <p className="dim" style={{ marginTop: 0, marginBottom: 18, fontSize: 14 }}>
        Hand it any OpenAPI spec. It builds a hardened MCP server in your browser, runs the
        audit, then drops you into a live agent so you can use it. No accounts, no install.
      </p>

      <label style={{ fontFamily: "monospace", fontSize: 12, letterSpacing: 1, color: "#aaa" }}>
        SPEC URL
      </label>
      <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
        <input
          value={url}
          onInput={(e): void => setUrl((e.target as HTMLInputElement).value)}
          onKeyDown={(e): void => { if ((e as KeyboardEvent).key === "Enter") void submitUrl(url); }}
          placeholder="https://…/openapi.json"
          disabled={busy}
          style={{ flex: 1, padding: "8px 10px", background: "#0c0c0c", color: "#eee", border: "1px solid #333", borderRadius: 4, fontFamily: "monospace" }}
        />
        <button onClick={(): Promise<void> => submitUrl(url)} disabled={busy || url.trim().length === 0}>
          {busy ? "…" : "build →"}
        </button>
      </div>

      <div style={{ marginTop: 6, fontSize: 12 }}>
        <span className="dim">try </span>
        {EXAMPLE_URLS.map((ex, i) => (
          <span key={ex.url}>
            <button
              style={{ all: "unset", cursor: "pointer", color: "#e9c46a", textDecoration: "underline dotted" }}
              onClick={(): Promise<void> => submitUrl(ex.url)}
              disabled={busy}
            >
              {ex.label}
            </button>
            {i < EXAMPLE_URLS.length - 1 && <span className="dim"> · </span>}
          </span>
        ))}
      </div>

      <details style={{ marginTop: 20 }}>
        <summary style={{ cursor: "pointer", color: "#aaa", fontFamily: "monospace", fontSize: 12, letterSpacing: 1 }}>
          OR PASTE JSON
        </summary>
        <textarea
          placeholder="Paste OpenAPI 3.x JSON…"
          value={paste}
          onInput={(e): void => setPaste((e.target as HTMLTextAreaElement).value)}
          disabled={busy}
          style={{ width: "100%", minHeight: 140, marginTop: 8, padding: 10, background: "#0c0c0c", color: "#eee", border: "1px solid #333", borderRadius: 4, fontFamily: "monospace", fontSize: 12 }}
        />
        <button onClick={submitPaste} disabled={busy || paste.trim().length === 0} style={{ marginTop: 6 }}>
          build from pasted JSON →
        </button>
      </details>

      {error && (
        <p style={{ marginTop: 12, color: "#e57a7a", fontSize: 13 }}>
          {error}
        </p>
      )}
    </section>
  );
}
